// Sprint 6 — Reservas Recurrentes

import { css, cx } from "styled-system/css";
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { DashboardPageShell } from "@/components/dashboard/dashboard-page-shell";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RecurringBookingTable } from "@/components/dashboard/recurring-booking-table";
import {
  CreateRecurringModal,
  EditRecurringModal,
  type RecurringRow,
} from "@/components/dashboard/create-recurring-modal";
import { useToast } from "@/hooks/useToast";
import { useConfirm } from "@/hooks/useConfirm";

type ListResponse = {
  items: RecurringRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};

export function DashboardRecurring() {
  const { refresh } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const effectiveProfessionalId = searchParams.get("professionalId");
  const professionalQuery = effectiveProfessionalId
    ? `?professionalId=${encodeURIComponent(effectiveProfessionalId)}`
    : "";

  const [tab, setTab] = useState<"active" | "finished">("active");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({ active: 0, finished: 0 });
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<RecurringRow | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const loadCounts = useCallback(async () => {
    const qs = (status: string) =>
      `${professionalQuery}${professionalQuery ? "&" : "?"}status=${status}&page=1&limit=1`;
    const [ra, rf] = await Promise.all([
      fetchWithSessionSync(`/api/dashboard/recurring-bookings${qs("active")}`, { credentials: "include" }, refresh),
      fetchWithSessionSync(`/api/dashboard/recurring-bookings${qs("finished")}`, { credentials: "include" }, refresh),
    ]);
    const ja = ra.ok ? ((await ra.json()) as ListResponse) : null;
    const jf = rf.ok ? ((await rf.json()) as ListResponse) : null;
    setCounts({ active: ja?.total ?? 0, finished: jf?.total ?? 0 });
  }, [refresh, professionalQuery]);

  const loadList = useCallback(async () => {
    setLoading(true);
    const sp = new URLSearchParams({
      status: tab === "active" ? "active" : "finished",
      page: String(page),
      limit: "10",
    });
    if (q.trim()) sp.set("q", q.trim());
    const prefix = professionalQuery ? `${professionalQuery}&` : "?";
    const r = await fetchWithSessionSync(
      `/api/dashboard/recurring-bookings${prefix}${sp.toString()}`,
      { credentials: "include" },
      refresh,
    );
    setLoading(false);
    if (!r.ok) {
      setList({ items: [], total: 0, page: 1, limit: 10, totalPages: 1 });
      return;
    }
    setList((await r.json()) as ListResponse);
  }, [tab, page, q, refresh, professionalQuery]);

  useEffect(() => {
    void loadCounts();
  }, [loadCounts]);

  useEffect(() => {
    void loadList();
  }, [loadList]);

  useEffect(() => {
    setPage(1);
  }, [tab, q]);

  async function onDeactivate(row: RecurringRow) {
    const ok = await confirm(
      `¿Desactivar la serie de ${row.clientName}? Se cancelarán los turnos futuros generados por esta serie.`,
    );
    if (!ok) return;
    const r = await fetchWithSessionSync(
      `/api/dashboard/recurring-bookings/${row.id}${professionalQuery}`,
      { method: "DELETE", credentials: "include" },
      refresh,
    );
    if (!r.ok) {
      toast.error("No se pudo desactivar");
      return;
    }
    toast.success("Serie desactivada");
    void loadCounts();
    void loadList();
  }

  return (
    <DashboardPageShell
      eyebrow="Agenda"
      title="Reservas recurrentes"
      description="Series automáticas con hasta 12 turnos generados por alta. Respetan bloqueos y tu horario de trabajo."
    >
      <div className={css({ display: "flex", flexWrap: "wrap", gap: "3", justifyContent: "space-between", mb: "4" })}>
        <div className={css({ display: "flex", gap: "2", flexWrap: "wrap" })}>
          <button
            type="button"
            onClick={() => setTab("active")}
            className={cx(
              css({
                py: "2",
                px: "4",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                borderWidth: "1px",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }),
              tab === "active"
                ? css({ bg: "#3730a3", color: "white", borderColor: "#3730a3" })
                : css({ bg: "white", color: "#475569", borderColor: "#e2e8f0", _hover: { bg: "#f8fafc" } }),
            )}
          >
            Activas ({counts.active})
          </button>
          <button
            type="button"
            onClick={() => setTab("finished")}
            className={cx(
              css({
                py: "2",
                px: "4",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                borderWidth: "1px",
                cursor: "pointer",
                transition: "background 0.15s ease",
              }),
              tab === "finished"
                ? css({ bg: "#3730a3", color: "white", borderColor: "#3730a3" })
                : css({ bg: "white", color: "#475569", borderColor: "#e2e8f0", _hover: { bg: "#f8fafc" } }),
            )}
          >
            Finalizadas ({counts.finished})
          </button>
        </div>
        <Button
          variant="primary"
          type="button"
          onClick={() => setCreateOpen(true)}
          className={css({ display: "inline-flex", alignItems: "center", gap: "2" })}
        >
          <Plus size={18} strokeWidth={2} aria-hidden />
          Crear nueva
        </Button>
      </div>

      <div className={css({ mb: "4" })}>
        <Input
          placeholder="Buscar por nombre o email del cliente…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className={css({ maxW: "420px" })}
        />
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
        >
          <RecurringBookingTable
            rows={list?.items ?? []}
            loading={loading}
            page={list?.page ?? 1}
            totalPages={list?.totalPages ?? 1}
            onPageChange={setPage}
            onEdit={(row) => {
              setEditRow(row);
              setEditOpen(true);
            }}
            onDeactivate={onDeactivate}
          />
        </motion.div>
      </AnimatePresence>

      <CreateRecurringModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        professionalQuery={professionalQuery}
        onCreated={(result) => {
          if (result.skipped > 0) {
            toast.info(`Se crearon ${result.created} de ${result.total} turnos. ${result.skipped} fechas no estaban disponibles.`);
          } else {
            toast.success(`Serie creada: ${result.created} turnos generados.`);
          }
          void loadCounts();
          void loadList();
        }}
      />

      <EditRecurringModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditRow(null);
        }}
        row={editRow}
        professionalQuery={professionalQuery}
        onSaved={() => {
          toast.success("Cambios guardados");
          void loadList();
        }}
      />
    </DashboardPageShell>
  );
}
